import { BiometryType, NativeBiometric } from "@capgo/capacitor-native-biometric";

const DEFAULT_SERVER = "com.ironflytechnologies.glossvault";

// Check if biometric authentication is available. The OS decides which enrolled
// sensor (fingerprint, Face ID, etc.) actually gets used.
export const checkBiometric = async () => {
  return await NativeBiometric.isAvailable();
};

export type BiometricIcon = "face-id" | "fingerprint";

// Icon matching the enrolled biometry type reported by checkBiometric(). Face-based types
// (iOS Face ID, Android face authentication) get "face-id"; everything else falls back to
// "fingerprint".
export const biometryTypeToIcon = (biometryType: BiometryType): BiometricIcon => {
  return biometryType === BiometryType.FACE_ID || biometryType === BiometryType.FACE_AUTHENTICATION
    ? "face-id"
    : "fingerprint";
};

// Perform biometric verification
export const verify = async () => {
  return await NativeBiometric.verifyIdentity({
    reason: "For secure access to your account",
    title: "Authentication",
    subtitle: "Log onto Gloss App",
    description: "Authenticate to continue. Max attempts: 5",
    maxAttempts: 5,
  });
};

// Store credentials securely
export const saveCredentials = async (username: string, password: string) => {
  return await NativeBiometric.setCredentials({
    username,
    password,
    server: DEFAULT_SERVER,
  });
};

// Retrieve stored credentials
export const getCredentials = async () => {
  return await NativeBiometric.getCredentials({
    server: DEFAULT_SERVER,
  });
};

// Delete stored credentials
export const deleteCredentials = async () => {
  await NativeBiometric.deleteCredentials({
    server: DEFAULT_SERVER,
  });
};

export const isCredentialsSaved = async () => {
  const res = await NativeBiometric.isCredentialsSaved({
    server: DEFAULT_SERVER,
  });
  return res.isSaved;
};

// Pre-consolidation (#3379..#4132), Face ID credentials were stored under this separate
// keychain slot. Migrate any leftover credential into the single generic slot, then
// remove the old one. Safe to call repeatedly — caller should still gate on a
// persisted "already migrated" flag to avoid the extra native calls on every load.
// The legacy slot is only deleted after a successful copy (or when there's nothing to
// copy) — if the copy step throws, the legacy credential is left in place so the caller
// can retry on next launch instead of losing it silently.
const LEGACY_FACE_ID_SERVER = "com.ironflytechnologies.glossvault.faceid";

// Login and Settings both trigger the migration on init, and the login page's run isn't
// cancelled by navigation — concurrent callers share the in-flight run instead of racing
// each other's keychain writes and legacy-slot delete. Cleared once settled so a failed
// run can be retried.
let legacyFaceIdMigrationInFlight: Promise<void> | null = null;

export const migrateLegacyFaceIdCredential = (): Promise<void> => {
  legacyFaceIdMigrationInFlight ??= runLegacyFaceIdMigration().finally(() => {
    legacyFaceIdMigrationInFlight = null;
  });
  return legacyFaceIdMigrationInFlight;
};

const runLegacyFaceIdMigration = async () => {
  const legacy = await NativeBiometric.isCredentialsSaved({ server: LEGACY_FACE_ID_SERVER });
  if (!legacy.isSaved) {
    return;
  }

  if (!(await isCredentialsSaved())) {
    const { username, password } = await NativeBiometric.getCredentials({
      server: LEGACY_FACE_ID_SERVER,
    });
    await saveCredentials(username, password);
  }

  await NativeBiometric.deleteCredentials({ server: LEGACY_FACE_ID_SERVER });
};
